/**
 * The hamburger, and the drawer it opens.
 *
 * It owns the open state so the button and the panel travel together: two
 * different bars host this control — the home header, which has no masthead above
 * it, and the page masthead on every other screen — and neither should have to
 * carry drawer state it does not otherwise care about.
 *
 * The drawer is mounted only while open, so its slide-in animation replays on
 * every open rather than running once at first paint.
 */

import { useState } from 'react';

import MenuDrawer from '@/components/MenuDrawer';
import { useT } from '@/i18n';

/** The hamburger glyph, matching the one the pre-auth screens already use. */
function MenuGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

/**
 * Render the menu button.
 *
 * @param className The host bar's own class for the control, so each bar sizes and
 *   spaces it to its own row rather than this component guessing.
 */
export default function MenuButton({ className }: { className: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={t('menu.open')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <MenuGlyph />
      </button>
      {open && <MenuDrawer onClose={() => setOpen(false)} />}
    </>
  );
}
